import { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import { Mail, MessageCircle, Clock, ArrowRight } from "lucide-react";

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_URL =
  "https://calendly.com/viralujpl/konsultacja?hide_event_type_details=1&hide_gdpr_banner=1&text_color=1f2937&primary_color=1a8cff&background_color=ffffff";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const CONTACT_CARDS = [
  {
    icon: Mail,
    title: "Email",
    description: "Napisz do nas w dowolnej sprawie",
    action: "kontakt@viraluj.pl",
    href: "mailto:kontakt@viraluj.pl",
  },
  {
    icon: MessageCircle,
    title: "Instagram",
    description: "Napisz do nas na DM",
    action: "@viraluj.pl",
    href: "https://instagram.com/viraluj.pl",
  },
  {
    icon: Clock,
    title: "Czas odpowiedzi",
    description: "Odpisujemy w ciągu 24 godzin",
    action: "Pon-Pt, 9:00-17:00",
    href: null,
  },
] as const;

const Kontakt = () => {
  useEffect(() => {
    if (document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`)) return;
    const script = document.createElement("script");
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="min-h-screen text-gray-900">
      <Header />

      <section className="relative min-h-screen flex items-center justify-center pt-24 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FEF3EC] via-[#FABB96] to-[#F5A07A]" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 w-full">
          {/* Heading */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4 text-gray-900">
              Skontaktuj się z nami<span className="text-primary">.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-700 max-w-md mx-auto font-medium">
              Masz pytanie? Chcesz współpracować? Odezwij się!
            </p>
          </motion.div>

          {/* Calendly inline widget */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="max-w-3xl mx-auto"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Umów rozmowę<span className="text-primary">.</span>
              </h2>
              <p className="text-sm md:text-base text-gray-700 mt-2">
                Zarezerwuj 20-minutowy slot, który Ci pasuje.
              </p>
            </div>
            <style>{`
              .calendly-inline-widget .calendly-spinner > div {
                background-color: #1a8cff !important;
              }
            `}</style>
            <div
              className="calendly-inline-widget rounded-2xl overflow-hidden"
              data-url={CALENDLY_URL}
              style={{ minWidth: 320, height: 700 }}
            />
          </motion.div>

          {/* Cards */}
          <div className="grid sm:grid-cols-3 gap-5 max-w-3xl mx-auto mt-12">
            {CONTACT_CARDS.map((card, i) => (
              <motion.div
                key={card.title}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={i + 2}
                className="group rounded-2xl p-7 text-center space-y-4 transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: "rgba(255, 255, 255, 0.45)",
                  backdropFilter: "blur(24px) saturate(150%)",
                  WebkitBackdropFilter: "blur(24px) saturate(150%)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors duration-300">
                  <card.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-lg text-gray-900">{card.title}</h3>
                <p className="text-sm text-gray-600">{card.description}</p>
                {card.href ? (
                  <a
                    href={card.href}
                    target={card.href.startsWith("http") ? "_blank" : undefined}
                    rel={card.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm hover:gap-2.5 transition-all duration-200"
                  >
                    {card.action}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <p className="text-sm font-semibold text-gray-800">{card.action}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Kontakt;
